import React, { useState, useEffect, useMemo } from 'react';
import {
    FiTrendingUp, FiTrendingDown, FiFilter, FiDownload,
    FiRefreshCw, FiSearch, FiEye, FiCalendar, FiCreditCard, FiUsers,
    FiArrowUpCircle, FiArrowDownCircle, FiClock, FiCheckCircle, FiXCircle,
    FiDollarSign, FiPieChart, FiBarChart, FiBarChart2, FiActivity, FiTarget,
    FiSettings, FiMoreHorizontal, FiChevronDown, FiChevronUp, FiStar,
    FiTrendingDown as FiArrowDown, FiTrendingUp as FiArrowUp,
    FiChevronLeft,
    FiChevronRight
  } from 'react-icons/fi';
import { exportToCSV } from '../utils/csvExport';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { FaRupeeSign } from 'react-icons/fa';
import moment from 'moment';

const FinanceModern = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  
  // State for API data
  const [dashboardData, setDashboardData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [vendorEarnings, setVendorEarnings] = useState([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  
  // Calendar state
  const [currentCalendarDate, setCurrentCalendarDate] = useState(moment());
  const [calendarData, setCalendarData] = useState([]);
  const [convenienceFeesData, setConvenienceFeesData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateBookings, setDateBookings] = useState([]);
  const [showDateModal, setShowDateModal] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    type: '',
    status: 'completed', // Default to completed bookings only
    paymentMethod: '',
    search: '',
    period: '30',
    vendorId: '',
    minAmount: '',
    maxAmount: ''
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalPages: 1,
    totalItems: 0
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');

  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.bookwie.com';

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem('adminToken') || localStorage.getItem('token');
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/finance/dashboard?period=${filters.period}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data.data);
      } else {
        console.error('Failed to fetch dashboard data');
        // Set empty data structure
        setDashboardData({
          totalMetrics: {
            totalRevenue: 0,
            totalCommissionEarned: 0,
            totalBookings: 0,
            completedBookings: 0,
            totalWithdrawableBalance: 0,
            activeVendors: 0
          },
          periodMetrics: {
            periodBookings: 0,
            periodRevenue: 0,
            periodCommission: 0
          },
          topVendors: []
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set empty data structure on error
      setDashboardData({
        totalMetrics: {
          totalRevenue: 0,
          totalCommissionEarned: 0,
          totalBookings: 0,
          completedBookings: 0,
          totalWithdrawableBalance: 0,
          activeVendors: 0
        },
        periodMetrics: {
          periodBookings: 0,
          periodRevenue: 0,
          periodCommission: 0
        },
        topVendors: []
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch transactions
  const fetchTransactions = async (reset = false) => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams({
        page: reset ? 1 : pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(Object.entries(filters).filter(([key, value]) => value !== '' && key !== 'period'))
      });

      const response = await fetch(`${API_BASE_URL}/api/finance/transactions?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.data.transactions || []);
        setPagination(prev => ({
          ...prev,
          ...data.data.pagination,
          page: reset ? 1 : prev.page
        }));
      } else {
        console.error('Failed to fetch transactions');
        setTransactions([]);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch vendor earnings
  const fetchVendorEarnings = async (reset = false) => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams({
        page: reset ? 1 : pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(Object.entries(filters).filter(([key, value]) => value !== '' && key !== 'period'))
      });

      const response = await fetch(`${API_BASE_URL}/api/finance/vendor-earnings?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVendorEarnings(data.data.vendorEarnings || []);
        setPagination(prev => ({
          ...prev,
          ...data.data.pagination,
          page: reset ? 1 : prev.page
        }));
      } else {
        console.error('Failed to fetch vendor earnings');
        setVendorEarnings([]);
      }
    } catch (error) {
      console.error('Error fetching vendor earnings:', error);
      setVendorEarnings([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch withdrawal requests
  const fetchWithdrawalRequests = async (reset = false) => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams({
        page: reset ? 1 : pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(Object.entries(filters).filter(([key, value]) => value !== '' && key !== 'period'))
      });

      const response = await fetch(`${API_BASE_URL}/api/finance/withdrawal-requests?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWithdrawalRequests(data.data.withdrawalRequests || []);
        setPagination(prev => ({
          ...prev,
          ...data.data.pagination,
          page: reset ? 1 : prev.page
        }));
      } else {
        console.error('Failed to fetch withdrawal requests');
        setWithdrawalRequests([]);
      }
    } catch (error) {
      console.error('Error fetching withdrawal requests:', error);
      setWithdrawalRequests([]);
    } finally {
      setLoading(false);
    }
  };

  // Process withdrawal request
  const processWithdrawalRequest = async (requestId, action, data = {}) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/finance/withdrawal-requests/${requestId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action, ...data })
      });
      
      if (response.ok) {
        const result = await response.json();
        // Refresh withdrawal requests
        fetchWithdrawalRequests();
        setModalOpen(false);
        // Show success message
        alert(`Withdrawal request ${action}d successfully`);
      } else {
        console.error('Failed to process withdrawal request');
        alert('Failed to process withdrawal request');
      }
    } catch (error) {
      console.error('Error processing withdrawal request:', error);
      alert('Error processing withdrawal request');
    }
  };

  // Fetch calendar data
  const fetchCalendarData = async () => {
    try {
      const month = currentCalendarDate.month();
      const year = currentCalendarDate.year();
      
      const response = await fetch(`${API_BASE_URL}/api/finance/calendar-dashboard?month=${month}&year=${year}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCalendarData(data.data.calendarData || []);
        // Store convenience fees data separately
        setConvenienceFeesData(data.data.convenienceFeesAnalytics || []);
      } else {
        console.error('Failed to fetch calendar data');
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchDashboardData();
  }, [filters.period]);

  // Fetch calendar data when date changes
  useEffect(() => {
    fetchCalendarData();
    // Clear convenience fees data when changing months
    setConvenienceFeesData([]);
  }, [currentCalendarDate]);

  // Refetch data when active tab changes
  useEffect(() => {
    switch (activeTab) {
      case 'transactions':
        fetchTransactions(true);
        break;
      case 'vendors':
        fetchVendorEarnings(true);
        break;
      case 'withdrawals':
        fetchWithdrawalRequests(true);
        break;
      default:
        break;
    }
  }, [activeTab]);

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    setTimeout(() => {
      switch (activeTab) {
        case 'transactions':
          fetchTransactions();
          break;
        case 'vendors':
          fetchVendorEarnings();
          break;
        case 'withdrawals':
          fetchWithdrawalRequests();
          break;
        default:
          break;
      }
    }, 100);
  };

  // Refresh all data
  const refreshData = () => {
    fetchDashboardData();
    switch (activeTab) {
      case 'transactions':
        fetchTransactions(true);
        break;
      case 'vendors':
        fetchVendorEarnings(true);
        break;
      case 'withdrawals':
        fetchWithdrawalRequests(true);
        break;
      default:
        break;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status, type = 'transaction') => {
    const statusConfig = {
      transaction: {
        completed: 'bg-green-100 text-green-800 border-green-200',
        pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        failed: 'bg-red-100 text-red-800 border-red-200'
      },
      type: {
        credit: 'bg-green-100 text-green-800 border-green-200',
        debit: 'bg-red-100 text-red-800 border-red-200'
      },
      withdrawal: {
        pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        approved: 'bg-blue-100 text-blue-800 border-blue-200',
        completed: 'bg-green-100 text-green-800 border-green-200',
        rejected: 'bg-red-100 text-red-800 border-red-200'
      }
    };

    return statusConfig[type]?.[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Get status color for calendar
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'confirmed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Get payment method display name
  const getPaymentMethodDisplay = (method) => {
    switch (method) {
      case 'online': return 'Online Payment';
      case 'offline': return 'Cash/Offline';
      default: return method || 'N/A';
    }
  };

  // Get payment status display name
  const getPaymentStatusDisplay = (status) => {    
    switch (status) {
      case 'paid': return 'Paid';
      case 'pending': return 'Pending';
      case 'failed': return 'Failed';
      default: return status || 'N/A';
    }
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const startOfMonth = currentCalendarDate.clone().startOf('month');
    const endOfMonth = currentCalendarDate.clone().endOf('month');
    const startDate = startOfMonth.clone().startOf('week');
    const endDate = endOfMonth.clone().endOf('week');

    const days = [];
    let day = startDate.clone();

    while (day.isSameOrBefore(endDate)) {
      const dateStr = day.format('YYYY-MM-DD');
      const dayData = calendarData.find(d => d._id === dateStr);
      
      days.push({
        date: day.clone(),
        isCurrentMonth: day.isSame(currentCalendarDate, 'month'),
        isToday: day.isSame(moment(), 'day'),
        data: dayData
      });
      
      day.add(1, 'day');
    }

    return days;
  };

  // Handle date click
  const handleDateClick = async (date) => {
    try {
      const dateStr = date.format('YYYY-MM-DD');
      const response = await fetch(`${API_BASE_URL}/api/finance/date-bookings/${dateStr}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDateBookings(data.data.bookings || []);
        setSelectedDate(date);
        setShowDateModal(true);
      } else {
        console.error('Failed to fetch date bookings');
      }
    } catch (error) {
      console.error('Error fetching date bookings:', error);
    }
  };

  // Export data
  const exportToExcel = () => {
    setExportLoading(true);
    
    try {
      let exportData = [];
      let filename = '';

      switch (activeTab) {
        case 'transactions':
          exportData = transactions.map((transaction, index) => ({
            'S.No': index + 1,
            'Transaction ID': transaction._id,
            'Vendor Name': transaction.vendorName,
            'Store Name': transaction.storeName,
            'Customer Name': transaction.transaction?.customerName || 'N/A',
            'Amount': transaction.transaction?.amount,
            'Commission': transaction.transaction?.commissionAmount,
            'Convenience Fees': transaction.transaction?.convenienceFees || 0,
            'Type': transaction.transaction?.type,
            'Status': transaction.transaction?.status,
            'Payment Method': transaction.transaction?.paymentMethod,
            'Description': transaction.transaction?.description,
            'Transaction Time': formatDate(transaction.transaction?.transactionTime)
          }));
          filename = `Finance_Transactions_${new Date().toISOString().split('T')[0]}.xlsx`;
          break;
        case 'vendors':
          exportData = vendorEarnings.map((vendor, index) => ({
            'S.No': index + 1,
            'Vendor Name': vendor.vendorName,
            'Store Name': vendor.storeName,
            'Phone': vendor.vendorPhone,
            'Total Earnings': vendor.totalEarnings,
            'Total Commission': vendor.totalCommission || 0,
            'Total Convenience Fees': vendor.totalConvenienceFees || 0,
            'Withdrawable Balance': vendor.withdrawableBalance,
            'Pending Amount': vendor.pendingAmount,
            'Total Withdrawn': vendor.totalWithdrawn,
            'Total Transactions': vendor.totalTransactions,
            'Last Transaction': vendor.lastTransactionDate ? formatDate(vendor.lastTransactionDate) : 'N/A'
          }));
          filename = `Vendor_Earnings_${new Date().toISOString().split('T')[0]}.xlsx`;
          break;
        case 'withdrawals':
          exportData = withdrawalRequests.map((request, index) => ({
            'S.No': index + 1,
            'Request ID': request._id,
            'Vendor Name': request.vendorName,
            'Store Name': request.storeName,
            'Phone': request.vendorPhone,
            'Amount': request.withdrawalRequest?.amount,
            'Status': request.withdrawalRequest?.status,
            'Requested At': formatDate(request.withdrawalRequest?.requestedAt),
            'Processed At': request.withdrawalRequest?.processedAt ? formatDate(request.withdrawalRequest.processedAt) : 'N/A',
            'Notes': request.withdrawalRequest?.notes || 'N/A'
          }));
          filename = `Withdrawal_Requests_${new Date().toISOString().split('T')[0]}.csv`;
          break;
        default:
          return;
      }

      // Export to CSV
      exportToCSV(exportData, filename);
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExportLoading(false);
    }
  };

  // Period totals: the API returns one row per day, so sum every row
  // (reading [0] showed only the oldest day)
  const periodTotals = useMemo(() => {
    const rows = dashboardData?.revenueAnalytics || [];
    const sum = (key) => rows.reduce((total, row) => total + (row[key] || 0), 0);
    const commission = sum('totalCommission');
    const convenienceFees = sum('totalConvenienceFees');
    const platformDiscount = sum('totalPlatformDiscount');
    return {
      revenue: sum('totalRevenue'),
      bookings: sum('totalBookings'),
      commission,
      convenienceFees,
      vendorEarnings: sum('totalVendorEarnings'),
      couponDiscount: sum('totalCouponDiscount'),
      platformDiscount,
      netPlatformEarnings: commission + convenienceFees - platformDiscount,
      commissionRate: dashboardData?.commissionAnalytics?.[0]?.commissionRate
    };
  }, [dashboardData]);

  // Generate chart data from API response
  const chartData = useMemo(() => {
    if (!dashboardData) {
      return {
        revenue: [],
        transactions: [],
        paymentMethods: []
      };
    }

    // Generate revenue chart data from revenueAnalytics
    const revenueData = dashboardData.revenueAnalytics || [];
    const revenue = revenueData.map(item => ({
      name: new Date(item._id).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      revenue: item.totalRevenue || 0,
      commission: item.totalCommission || 0
    }));

    // Generate transaction frequency data from transactionTrends
    const transactions = [
      { name: 'Mon', transactions: 0 },
      { name: 'Tue', transactions: 0 },
      { name: 'Wed', transactions: 0 },
      { name: 'Thu', transactions: 0 },
      { name: 'Fri', transactions: 0 },
      { name: 'Sat', transactions: 0 },
      { name: 'Sun', transactions: 0 },
    ];

    // If we have transaction trends data, update the current day
    if (dashboardData.transactionTrends && dashboardData.transactionTrends.length > 0) {
      const today = new Date().getDay(); // 0 = Sunday, 1 = Monday, etc.
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const todayName = dayNames[today];
      
      const todayTransactions = dashboardData.transactionTrends.find(trend => 
        new Date(trend._id).toDateString() === new Date().toDateString()
      );
      
      if (todayTransactions) {
        const dayIndex = dayNames.indexOf(todayName);
        if (dayIndex !== -1) {
          transactions[dayIndex].transactions = todayTransactions.totalTransactions || 0;
        }
      }
    }

    // Generate payment methods data from paymentMethodStats
    const paymentMethods = [];
    if (dashboardData.paymentMethodStats) {
      dashboardData.paymentMethodStats.forEach(method => {
        if (method._id === 'online') {
          paymentMethods.push({
            name: 'Online',
            value: method.count || 0,
            color: '#3B82F6',
            revenue: method.revenue || 0
          });
        } else if (method._id === 'offline') {
          paymentMethods.push({
            name: 'Offline',
            value: method.count || 0,
            color: '#EF4444',
            revenue: method.revenue || 0
          });
        }
      });
    }

    return {
      revenue,
      transactions,
      paymentMethods
    };
  }, [dashboardData]);

  if (loading && !dashboardData) {
    return (
      <div className="py-32 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-600">Loading financial data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Header */}
      <div className="card px-6">
        <div className="">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">Financial Management</h1>
              <p className="text-gray-600 mt-1">Comprehensive financial dashboard and analytics</p>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Period Selector */}
              <div className="relative">
                <select
                  value={filters.period}
                  onChange={(e) => setFilters({...filters, period: e.target.value})}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                  <option value="365">Last year</option>
                </select>
                <FiChevronDown className="absolute right-2 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Export Button */}
              <button
                onClick={exportToExcel}
                disabled={exportLoading}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {exportLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <FiDownload className="mr-2 h-4 w-4" />
                    Export
                  </>
                )}
              </button>

              {/* Refresh Button */}
              <button
                onClick={refreshData}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <FiRefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex space-x-8 border-b border-gray-200">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: FiPieChart },
              { id: 'transactions', label: 'Transactions', icon: FiCreditCard },
              { id: 'vendors', label: 'Vendor Earnings', icon: FiUsers },
              { id: 'withdrawals', label: 'Withdrawals', icon: FiArrowDownCircle },
              { id: 'analytics', label: 'Analytics', icon: FiBarChart }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="mr-2 h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboardData && (
          <div className="space-y-8">
            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                          {/* Total Revenue */}
              <div className="bg-white rounded-3xl shadow-card p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Total Revenue</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                      {formatCurrency(periodTotals.revenue)}
                  </p>
                  <div className="flex items-center mt-2 text-green-600">
                    <FiTrendingUp size={16} className="mr-1" />
                    <span className="text-sm font-medium">Last {filters.period} days</span>
                  </div>
                </div>
                  <div className="p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
                    <FaRupeeSign size={24} className="text-green-600" />
                </div>
              </div>
            </div>

                          {/* Commission Earned */}
              <div className="bg-white rounded-3xl shadow-card p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Commission Earned</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                      {formatCurrency(periodTotals.commission)}
                  </p>
                  <div className="flex items-center mt-2 text-blue-600">
                    <FiTrendingUp size={16} className="mr-1" />
                      <span className="text-sm font-medium">{periodTotals.commissionRate != null ? `${periodTotals.commissionRate}% current rate` : 'Per-booking rate'}</span>
                  </div>
                </div>
                  <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                    <FiTarget size={24} className="text-blue-600" />
                </div>
              </div>
            </div>

                          {/* Convenience Fee Earned */}
              <div className="bg-white rounded-3xl shadow-card p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Convenience Fee Earned</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                      {formatCurrency(periodTotals.convenienceFees)}
                  </p>
                  <div className="flex items-center mt-2 text-green-600">
                    <FiTrendingUp size={16} className="mr-1" />
                      <span className="text-sm font-medium">Platform Fee</span>
                  </div>
                </div>
                  <div className="p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
                    <FaRupeeSign size={24} className="text-green-600" />
                </div>
              </div>
            </div>

                            {/* Total Bookings */}
              <div className="bg-white rounded-3xl shadow-card p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-600 text-sm font-medium">Total Bookings</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                      {periodTotals.bookings.toLocaleString()}
                  </p>
                  <div className="flex items-center mt-2 space-x-4">
                    <span className="text-sm text-green-600">
                        ✓ {periodTotals.bookings} completed
                    </span>
                  </div>
                </div>
                  <div className="p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
                    <FiActivity size={24} className="text-purple-600" />
                </div>
              </div>
            </div>

                            {/* Withdrawable Balance */}
              <div className="bg-white rounded-3xl shadow-card p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-600 text-sm font-medium">Total Vendor Earnings</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                      {formatCurrency(periodTotals.vendorEarnings)}
                  </p>
                  <div className="flex items-center mt-2 text-orange-600">
                    <FiClock size={16} className="mr-1" />
                      <span className="text-sm font-medium">Last {filters.period} days</span>
                  </div>
                </div>
                  <div className="p-3 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg">
                    <FiArrowUpCircle size={24} className="text-orange-600" />
                </div>
              </div>
            </div>

                            {/* Platform-funded discounts */}
              <div className="bg-white rounded-3xl shadow-card p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-600 text-sm font-medium">Platform-funded Discounts</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                      {formatCurrency(periodTotals.platformDiscount)}
                  </p>
                  <div className="flex items-center mt-2 text-red-600">
                    <FiTrendingDown size={16} className="mr-1" />
                      <span className="text-sm font-medium">of {formatCurrency(periodTotals.couponDiscount)} coupon discounts</span>
                  </div>
                </div>
                  <div className="p-3 bg-gradient-to-br from-red-50 to-red-100 rounded-lg">
                    <FaRupeeSign size={24} className="text-red-600" />
                </div>
              </div>
            </div>

                            {/* Net platform earnings */}
              <div className="bg-white rounded-3xl shadow-card p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-600 text-sm font-medium">Net Platform Earnings</p>
                  <p className={`text-2xl font-bold mt-1 ${periodTotals.netPlatformEarnings < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      {formatCurrency(periodTotals.netPlatformEarnings)}
                  </p>
                  <div className="flex items-center mt-2 text-gray-500">
                      <span className="text-sm font-medium">Commission + fees − platform discounts</span>
                  </div>
                </div>
                  <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                    <FiTarget size={24} className="text-blue-600" />
                </div>
              </div>
            </div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Revenue Trend Chart */}
              <div className="bg-white rounded-3xl shadow-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Revenue Trend</h3>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600">Revenue</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600">Commission</span>
                    </div>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={chartData.revenue}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip 
                      formatter={(value, name) => [formatCurrency(value), name]}
                      labelStyle={{ color: '#333' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      stackId="1"
                      stroke="#3B82F6" 
                      fill="#3B82F6"
                      fillOpacity={0.1}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="commission" 
                      stackId="1"
                      stroke="#10B981" 
                      fill="#10B981"
                      fillOpacity={0.1}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Transaction Frequency Chart */}
              <div className="bg-white rounded-3xl shadow-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Transaction Frequency</h3>
                  <FiBarChart className="text-gray-400" />
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData.transactions}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip 
                      formatter={(value) => [value, 'Transactions']}
                      labelStyle={{ color: '#333' }}
                    />
                    <Bar dataKey="transactions" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

                        {/* Payment Methods Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-3xl shadow-card p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Payment Methods</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={chartData.paymentMethods}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {chartData.paymentMethods.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [value, 'Bookings']} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {chartData.paymentMethods.map((method, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div 
                          className="w-3 h-3 rounded-full mr-2" 
                          style={{ backgroundColor: method.color }}
                        ></div>
                        <span className="text-sm text-gray-600">{method.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">{method.value} bookings</div>
                        <div className="text-xs text-gray-500">{formatCurrency(method.revenue)}</div>
                      </div>
                    </div>
                  ))}
                </div>
          </div>

              {/* Quick Stats */}
              <div className="lg:col-span-2 bg-white rounded-3xl shadow-card p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Quick Statistics</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">
                      {periodTotals.bookings}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Bookings (Last {filters.period} days)</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">
                      {formatCurrency(periodTotals.revenue)}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Revenue (Last {filters.period} days)</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600">
                      {dashboardData.vendorPerformance?.filter(v => v.performanceScore > 0).length || 0}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Active Vendors</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-orange-600">
                      {formatCurrency(periodTotals.commission)}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Commission (Last {filters.period} days)</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive Calendar View */}
            <div className="bg-white rounded-3xl shadow-card p-6">
              {/* Monthly Summary Stats */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">📅 Monthly Booking Calendar</h3>
                  <div className="text-sm text-gray-500">
                    {currentCalendarDate.format('MMMM YYYY')}
                  </div>
                </div>
                
                {/* Monthly Status Summary */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
                  {(() => {
                    // Aggregate data across ALL dates in the month
                    const monthData = calendarData.filter(item => 
                      moment(item._id, 'YYYY-MM-DD').month() === currentCalendarDate.month() &&
                      moment(item._id, 'YYYY-MM-DD').year() === currentCalendarDate.year()
                    );
                    
                    // Calculate totals across all dates
                    let totalBookings = 0;
                    let totalRevenue = 0;
                    let totalCommission = 0;
                    let totalConvenienceFees = 0;
                    const statusCounts = {
                      pending: 0,
                      confirmed: 0,
                      completed: 0,
                      cancelled: 0
                    };
                    
                    monthData.forEach(dateData => {
                      totalBookings += dateData.totalBookings || 0;
                      totalRevenue += dateData.totalRevenue || 0;
                      totalCommission += dateData.totalCommission || 0;
                      
                      if (dateData.statusBreakdown) {
                        dateData.statusBreakdown.forEach(status => {
                          if (statusCounts.hasOwnProperty(status.status)) {
                            statusCounts[status.status] += status.count || 0;
                          }
                        });
                      }
                    });
                    
                    // Calculate convenience fees from separate data
                    const monthConvenienceFees = convenienceFeesData.filter(item => 
                      moment(item._id, 'YYYY-MM-DD').month() === currentCalendarDate.month() &&
                      moment(item._id, 'YYYY-MM-DD').year() === currentCalendarDate.year()
                    );
                    
                    monthConvenienceFees.forEach(dateData => {
                      totalConvenienceFees += dateData.totalConvenienceFees || 0;
                    });
                    
                    return (
                      <>

                        
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 h-24 flex flex-col justify-between">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-green-600">Total Revenue</p>
                            <FaRupeeSign className="w-6 h-6 text-green-500" />
                          </div>
                          <p className="text-2xl font-bold text-green-900">₹{totalRevenue.toFixed(2)}</p>
                        </div>
                        
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 h-24 flex flex-col justify-between">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-yellow-600">Pending</p>
                            <FiClock className="w-6 h-6 text-yellow-500" />
                          </div>
                          <p className="text-2xl font-bold text-yellow-900">{statusCounts.pending}</p>
                        </div>
                        
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 h-24 flex flex-col justify-between">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-purple-600">Completed</p>
                            <FiCheckCircle className="w-6 h-6 text-purple-500" />
                          </div>
                          <p className="text-2xl font-bold text-purple-900">{statusCounts.completed}</p>
                        </div>
                        
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 h-24 flex flex-col justify-between">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-indigo-600">Confirmed</p>
                            <FiCheckCircle className="w-6 h-6 text-indigo-500" />
                          </div>
                          <p className="text-2xl font-bold text-indigo-900">{statusCounts.confirmed}</p>
                        </div>
                        
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 h-24 flex flex-col justify-between">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-red-600">Cancelled</p>
                            <FiXCircle className="w-6 h-6 text-red-500" />
                          </div>
                          <p className="text-2xl font-bold text-red-900">{statusCounts.cancelled}</p>
                        </div>
                        
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 h-24 flex flex-col justify-between">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-orange-600">Commission</p>
                            <FiTarget className="w-6 h-6 text-orange-500" />
                          </div>
                          <p className="text-2xl font-bold text-orange-900">₹{totalCommission.toFixed(2)}</p>
                        </div>
                        
                        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 h-24 flex flex-col justify-between">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-teal-600">Convenience Fee</p>
                            <FaRupeeSign className="w-6 h-6 text-teal-500" />
                          </div>
                          <p className="text-2xl font-bold text-teal-900">₹{totalConvenienceFees.toFixed(2)}</p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
              
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setCurrentCalendarDate(prev => prev.clone().subtract(1, 'month'))}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                  </button>
                  
                  <span className="text-sm font-medium text-gray-600">
                    {currentCalendarDate.format('MMMM YYYY')}
                  </span>
                  
                  <button
                    onClick={() => setCurrentCalendarDate(prev => prev.clone().add(1, 'month'))}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => setCurrentCalendarDate(moment())}
                    className="px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Today
                  </button>
                </div>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {/* Day Headers */}
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="p-2 text-center text-xs font-medium text-gray-500 bg-gray-50 rounded-lg">
                    {day}
                  </div>
                ))}
                
                {/* Calendar Days */}
                {generateCalendarDays().map((day, index) => (
                  <div
                    key={index}
                    className={`min-h-[80px] p-2 border border-gray-200 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                      !day.isCurrentMonth ? 'bg-gray-50 text-gray-400' : 'bg-white hover:border-blue-300'
                    } ${day.isToday ? 'ring-2 ring-blue-500' : ''}`}
                    onClick={() => day.isCurrentMonth && handleDateClick(day.date)}
                  >
                    <div className="text-xs font-medium mb-1">
                      {day.date.format('D')}
                    </div>
                    
                    {day.data && (
                      <div className="space-y-1">
                        {day.data.statusBreakdown.map((status, idx) => (
                          <div
                            key={idx}
                            className={`text-xs px-1 py-0.5 rounded-full border ${getStatusColor(status.status)}`}
                          >
                            {status.count} {status.status}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Legend */}
              <div className="mt-4 flex flex-wrap gap-3">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-yellow-100 border-2 border-yellow-200 rounded-full"></div>
                  <span className="text-xs text-gray-600">Pending</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-100 border-2 border-blue-200 rounded-full"></div>
                  <span className="text-xs text-gray-600">Confirmed</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-100 border-2 border-green-200 rounded-full"></div>
                  <span className="text-xs text-gray-600">Completed</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-100 border-2 border-red-200 rounded-full"></div>
                  <span className="text-xs text-gray-600">Cancelled</span>
                </div>
              </div>
            </div>

            {/* Top Vendors Table */}
            {dashboardData.topVendors && dashboardData.topVendors.length > 0 && (
              <div className="bg-white rounded-3xl shadow-card p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Top Performing Vendors</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vendor
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Earnings
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Commission
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Convenience Fees
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Transactions
                        </th>                      
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dashboardData.topVendors.map((vendor, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold">
                                  {vendor.vendorName?.charAt(0) || '?'}
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{vendor.vendorName}</div>
                                <div className="text-sm text-gray-500">{vendor.storeName}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(vendor.periodEarnings)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {formatCurrency(vendor.periodCommission)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {formatCurrency(vendor.periodConvenienceFees || 0)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {vendor.transactionCount}
                            </div>
                          </td>                         
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="space-y-6">
            {/* Filters Section */}
            <div className="bg-white rounded-3xl shadow-card p-6">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-4">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search transactions..."
                      value={filters.search}
                      onChange={(e) => setFilters({...filters, search: e.target.value})}
                      className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <FiSearch className="absolute left-3 top-3 text-gray-400" />
                  </div>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <FiFilter className="mr-2" />
                    Filters
                    {showFilters ? <FiChevronUp className="ml-2" /> : <FiChevronDown className="ml-2" />}
                  </button>
                </div>
              </div>

              {/* Advanced Filters */}
              {showFilters && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={filters.startDate}
                        onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                      <input
                        type="date"
                        value={filters.endDate}
                        onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <select
                        value={filters.type}
                        onChange={(e) => setFilters({...filters, type: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">All Types</option>
                        <option value="credit">Credit</option>
                        <option value="debit">Debit</option>
                        <option value="commission">Commission</option>
                        <option value="withdrawal">Withdrawal</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={filters.status}
                        onChange={(e) => setFilters({...filters, status: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">All Status</option>
                        <option value="completed">Completed</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Transactions Table */}
            <div className="bg-white rounded-3xl shadow-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vendor Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-8 text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                        </td>
                      </tr>
                    ) : transactions.length > 0 ? (
                      transactions.map((transaction) => (
                        <tr key={transaction._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-purple-400 to-purple-600 flex items-center justify-center text-white font-semibold">
                                  {transaction.vendorName?.charAt(0) || '?'}
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{transaction.vendorName}</div>
                                <div className="text-sm text-gray-500">{transaction.storeName}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(transaction.transaction?.amount || 0)}
                            </div>
                            {transaction.transaction?.commissionAmount > 0 && (
                              <div className="text-xs text-gray-500">
                                Commission: {formatCurrency(transaction.transaction.commissionAmount)}
                              </div>
                            )}
                            {transaction.transaction?.convenienceFees > 0 && (
                              <div className="text-xs text-gray-500">
                                Convenience Fee: {formatCurrency(transaction.transaction.convenienceFees)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(transaction.transaction?.type, 'type')}`}>
                              {transaction.transaction?.type === 'credit' ? (
                                <FiArrowUpCircle className="mr-1" size={12} />
                              ) : (
                                <FiArrowDownCircle className="mr-1" size={12} />
                              )}
                              {transaction.transaction?.type?.charAt(0).toUpperCase() + transaction.transaction?.type?.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(transaction.transaction?.status, 'transaction')}`}>
                              {transaction.transaction?.status?.charAt(0).toUpperCase() + transaction.transaction?.status?.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(transaction.transaction?.transactionTime)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button 
                              onClick={() => {
                                setSelectedItem(transaction);
                                setModalType('transaction');
                                setModalOpen(true);
                              }}
                              className="text-blue-600 hover:text-blue-900 transition-colors"
                            >
                              <FiEye className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                          No transactions found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {!loading && transactions.length > 0 && (
                <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.totalItems)} of {pagination.totalItems} transactions
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      Previous
                    </button>
                    
                    <span className="px-3 py-1 bg-blue-600 text-white rounded-md">
                      {pagination.page}
                    </span>
                    
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages}
                      className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Vendor Earnings Tab */}
        {activeTab === 'vendors' && (
          <div className="space-y-6">
            {/* Filters Section */}
            <div className="bg-white rounded-3xl shadow-card p-6">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search vendors..."
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                    className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <FiSearch className="absolute left-3 top-3 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Vendor Earnings Table */}
            <div className="bg-white rounded-3xl shadow-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vendor Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Earnings
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Available Balance
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pending Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transactions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Performance
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-8 text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                        </td>
                      </tr>
                    ) : vendorEarnings.length > 0 ? (
                      vendorEarnings.map((vendor) => (
                        <tr key={vendor.vendorId} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-green-400 to-green-600 flex items-center justify-center text-white font-semibold">
                                  {vendor.vendorName?.charAt(0) || '?'}
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{vendor.vendorName}</div>
                                <div className="text-sm text-gray-500">{vendor.storeName}</div>
                                <div className="text-xs text-gray-400">{vendor.vendorPhone}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(vendor.totalEarnings)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-green-600 font-medium">
                              {formatCurrency(vendor.withdrawableBalance)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-orange-600 font-medium">
                              {formatCurrency(vendor.pendingAmount)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{vendor.totalTransactions} total</div>
                            {vendor.pendingWithdrawals > 0 && (
                              <div className="text-xs text-orange-600">{vendor.pendingWithdrawals} pending withdrawals</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                <div 
                                  className="bg-green-500 h-2 rounded-full" 
                                  style={{ width: `${Math.min((vendor.totalEarnings / 100000) * 100, 100)}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-500">
                                {Math.min(Math.round((vendor.totalEarnings / 100000) * 100), 100)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                          No vendor earnings data found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {!loading && vendorEarnings.length > 0 && (
                <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.totalItems)} of {pagination.totalItems} vendors
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      Previous
                    </button>
                    
                    <span className="px-3 py-1 bg-blue-600 text-white rounded-md">
                      {pagination.page}
                    </span>
                    
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages}
                      className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Withdrawals Tab */}
        {activeTab === 'withdrawals' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-3xl shadow-card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Pending Requests</p>
                    <p className="text-2xl font-bold text-yellow-600 mt-1">
                      {withdrawalRequests.filter(r => r.withdrawalRequest?.status === 'pending').length}
                    </p>
                  </div>
                  <FiClock className="text-yellow-600" size={24} />
                </div>
              </div>

              <div className="bg-white rounded-3xl shadow-card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Approved</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">
                      {withdrawalRequests.filter(r => r.withdrawalRequest?.status === 'approved').length}
                    </p>
                  </div>
                  <FiCheckCircle className="text-blue-600" size={24} />
                </div>
              </div>

              <div className="bg-white rounded-3xl shadow-card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Completed</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      {withdrawalRequests.filter(r => r.withdrawalRequest?.status === 'completed').length}
                    </p>
                  </div>
                  <FiCheckCircle className="text-green-600" size={24} />
                </div>
              </div>

              <div className="bg-white rounded-3xl shadow-card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Rejected</p>
                    <p className="text-2xl font-bold text-red-600 mt-1">
                      {withdrawalRequests.filter(r => r.withdrawalRequest?.status === 'rejected').length}
                    </p>
                  </div>
                  <FiXCircle className="text-red-600" size={24} />
                </div>
              </div>
            </div>

            {/* Withdrawal Requests Table */}
            <div className="bg-white rounded-3xl shadow-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vendor Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Requested Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                        </td>
                      </tr>
                    ) : withdrawalRequests.length > 0 ? (
                      withdrawalRequests.map((request) => (
                        <tr key={request._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-orange-400 to-orange-600 flex items-center justify-center text-white font-semibold">
                                  {request.vendorName?.charAt(0) || '?'}
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{request.vendorName}</div>
                                <div className="text-sm text-gray-500">{request.storeName}</div>
                                <div className="text-xs text-gray-400">{request.vendorPhone}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(request.withdrawalRequest?.amount || 0)}
                            </div>
                            <div className="text-xs text-gray-500">
                              Available: {formatCurrency(request.currentBalance || 0)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(request.withdrawalRequest?.status, 'withdrawal')}`}>
                              {request.withdrawalRequest?.status?.charAt(0).toUpperCase() + request.withdrawalRequest?.status?.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(request.withdrawalRequest?.requestedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button 
                                onClick={() => {
                                  setSelectedItem(request);
                                  setModalType('withdrawal');
                                  setModalOpen(true);
                                }}
                                className="text-blue-600 hover:text-blue-900 transition-colors"
                              >
                                <FiEye className="h-5 w-5" />
                              </button>
                              {request.withdrawalRequest?.status === 'pending' && (
                                <>
                                  <button 
                                    onClick={() => processWithdrawalRequest(request._id, 'approve')}
                                    className="text-green-600 hover:text-green-900 transition-colors"
                                  >
                                    <FiCheckCircle className="h-5 w-5" />
                                  </button>
                                  <button 
                                    onClick={() => processWithdrawalRequest(request._id, 'reject', { rejectionReason: 'Insufficient verification' })}
                                    className="text-red-600 hover:text-red-900 transition-colors"
                                  >
                                    <FiXCircle className="h-5 w-5" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                          No withdrawal requests found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}



        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-8">
            <div className="text-center py-12">
              <FiBarChart className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Advanced Analytics</h3>
              <p className="mt-1 text-sm text-gray-500">
                Detailed financial analytics and reports will be available here.
              </p>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {modalOpen && selectedItem && (
          <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center border-b pb-4 mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {modalType === 'transaction' ? 'Transaction Details' : 'Withdrawal Request Details'}
                  </h3>
                  <button
                    onClick={() => setModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FiXCircle className="h-6 w-6" />
                  </button>
                </div>

                {modalType === 'transaction' && selectedItem.transaction && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-500">Transaction ID</p>
                        <p className="font-medium">{selectedItem._id}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-500">Vendor</p>
                        <p className="font-medium">{selectedItem.vendorName}</p>
                        <p className="text-sm text-gray-600">{selectedItem.storeName}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-500">Amount</p>
                        <p className="font-medium text-lg">{formatCurrency(selectedItem.transaction.amount)}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-500">Commission</p>
                        <p className="font-medium">{formatCurrency(selectedItem.transaction.commissionAmount)}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-500">Convenience Fees</p>
                        <p className="font-medium">{formatCurrency(selectedItem.transaction.convenienceFees || 0)}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-500">Type</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(selectedItem.transaction.type, 'type')}`}>
                          {selectedItem.transaction.type?.charAt(0).toUpperCase() + selectedItem.transaction.type?.slice(1)}
                        </span>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-500">Status</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(selectedItem.transaction.status, 'transaction')}`}>
                          {selectedItem.transaction.status?.charAt(0).toUpperCase() + selectedItem.transaction.status?.slice(1)}
                        </span>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-500">Payment Method</p>
                        <p className="font-medium">{selectedItem.transaction.paymentMethod}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-500">Date & Time</p>
                        <p className="font-medium">{formatDate(selectedItem.transaction.transactionTime)}</p>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-500">Description</p>
                      <p className="font-medium">{selectedItem.transaction.description}</p>
                    </div>
                  </div>
                )}

                {modalType === 'withdrawal' && selectedItem.withdrawalRequest && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-gray-500">Request ID</p>
                          <p className="font-medium">{selectedItem._id}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-500">Vendor</p>
                          <p className="font-medium">{selectedItem.vendorName}</p>
                          <p className="text-sm text-gray-600">{selectedItem.storeName}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-500">Amount Requested</p>
                          <p className="font-medium text-lg">{formatCurrency(selectedItem.withdrawalRequest.amount)}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-500">Available Balance</p>
                          <p className="font-medium text-green-600">{formatCurrency(selectedItem.currentBalance)}</p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-gray-500">Status</p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(selectedItem.withdrawalRequest.status, 'withdrawal')}`}>
                            {selectedItem.withdrawalRequest.status?.charAt(0).toUpperCase() + selectedItem.withdrawalRequest.status?.slice(1)}
                          </span>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-500">Requested Date</p>
                          <p className="font-medium">{formatDate(selectedItem.withdrawalRequest.requestedAt)}</p>
                        </div>
                        
                        {selectedItem.withdrawalRequest.processedAt && (
                          <div>
                            <p className="text-sm text-gray-500">Processed Date</p>
                            <p className="font-medium">{formatDate(selectedItem.withdrawalRequest.processedAt)}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {selectedItem.bankDetails && (
                      <div>
                        <p className="text-sm text-gray-500 mb-2">Bank Details</p>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-gray-500">Account Holder</p>
                              <p className="font-medium">{selectedItem.bankDetails.account_holder_name}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Account Number</p>
                              <p className="font-medium">****{selectedItem.bankDetails.account_number?.slice(-4)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">IFSC Code</p>
                              <p className="font-medium">{selectedItem.bankDetails.ifsc_code}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Bank Name</p>
                              <p className="font-medium">{selectedItem.bankDetails.bank_name}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedItem.withdrawalRequest.notes && (
                      <div>
                        <p className="text-sm text-gray-500">Notes</p>
                        <p className="font-medium">{selectedItem.withdrawalRequest.notes}</p>
                      </div>
                    )}

                    {selectedItem.withdrawalRequest.status === 'pending' && (
                      <div className="flex space-x-4 pt-4 border-t">
                        <button
                          onClick={() => processWithdrawalRequest(selectedItem._id, 'approve')}
                          className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Approve Request
                        </button>
                        <button
                          onClick={() => processWithdrawalRequest(selectedItem._id, 'reject', { rejectionReason: 'Manual review required' })}
                          className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                        >
                          Reject Request
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Date Bookings Modal */}
        {showDateModal && selectedDate && (
          <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center border-b pb-4 mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">
                    Bookings for {selectedDate.format('MMMM DD, YYYY')}
                  </h3>
                  <button
                    onClick={() => setShowDateModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FiXCircle className="h-6 w-6" />
                  </button>
                </div>

                {/* Bookings Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Booking Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vendor
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Payment Mode
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Payment Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Time
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dateBookings.length > 0 ? (
                        dateBookings.map((booking) => (
                          <tr key={booking.bookingId} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  #{booking.bookingNumber}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {booking.customerName}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {booking.customerPhone}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {booking.vendorName}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {booking.storeName}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {formatCurrency(booking.totalAmount)}
                              </div>
                              <div className="text-xs text-gray-500">
                                Commission: {formatCurrency(booking.commissionAmount)}
                              </div>
                              <div className="text-xs text-gray-500">
                                Convenience Fee: {formatCurrency(booking.convenienceFees)}
                              </div>
                              {booking.couponDiscount > 0 && (
                                <div className="text-xs text-gray-500">
                                  Coupon {booking.couponCode}: −{formatCurrency(booking.couponDiscount)} ({booking.couponFundedBy === 'vendor' ? 'vendor-funded' : 'platform-funded'})
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {getPaymentMethodDisplay(booking.paymentMethod)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                                booking.paymentStatus === 'paid' ? 'bg-green-100 text-green-800 border-green-200' :
                                booking.paymentStatus === 'pending' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                                booking.paymentStatus === 'failed' ? 'bg-red-100 text-red-800 border-red-200' :
                                'bg-gray-100 text-gray-800 border-gray-200'
                              }`}>
                                {getPaymentStatusDisplay(booking.paymentStatus)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(booking.status)}`}>
                                {booking.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {booking.timeSlot}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                            No bookings found for this date
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinanceModern;